/**
 * Shared OneSignal helpers and session dedupe state (native + web).
 */

import { parsePhoneNumber } from 'libphonenumber-js';
import type { User } from '@/api/types';
import type { ArtistDetailsData } from '@/types/artistDetails';
import {
  isValidArtistSlug,
  resolveArtistSlugSync,
} from '@/utils/resolveArtistSlug';

export interface SyncOneSignalUserParams {
  /** OneSignal External ID (artist slug, e.g. "qastar"). */
  artistId: string;
  email?: string;
  phone?: string;
}

/** Last External ID synced via OneSignal.login on this client. */
export let syncedExternalId: string | null = null;

/** Last email/SMS attached so contact updates can re-sync without redundant login. */
export let lastSyncedEmail: string | null = null;
export let lastSyncedPhone: string | null = null;

/** Serializes in-flight session sync calls. */
export let syncPromise: Promise<void> | null = null;

/**
 * OneSignal subscription IDs already POSTed to the Cockpit backend this browser session.
 * Prevents duplicate register requests for the same subscription.id.
 */
export const registeredBackendSubscriptionIds = new Set<string>();

/** Last subscription id registered (used to deactivate on logout). */
export let lastRegisteredBackendSubscriptionId: string | null = null;

export function clearOneSignalSessionState(): void {
  syncedExternalId = null;
  lastSyncedEmail = null;
  lastSyncedPhone = null;
  registeredBackendSubscriptionIds.clear();
  lastRegisteredBackendSubscriptionId = null;
}

export function markBackendSubscriptionRegistered(subscriptionId: string): void {
  registeredBackendSubscriptionIds.add(subscriptionId);
  lastRegisteredBackendSubscriptionId = subscriptionId;
}

export function wasBackendSubscriptionRegistered(subscriptionId: string): boolean {
  return registeredBackendSubscriptionIds.has(subscriptionId);
}

export function setSyncedExternalId(value: string): void {
  syncedExternalId = value;
}

export function setLastSyncedEmail(value: string): void {
  lastSyncedEmail = value;
}

export function setLastSyncedPhone(value: string): void {
  lastSyncedPhone = value;
}

/** Read and trim the OneSignal App ID from Vite env. */
export function getOneSignalAppId(): string {
  return import.meta.env.VITE_ONESIGNAL_APP_ID?.trim() ?? '';
}

/** Resolve artist slug for OneSignal External ID. */
export function resolveArtistId(
  details: ArtistDetailsData | null | undefined,
  user?: User | null,
): string | null {
  return resolveArtistSlugSync(details, user);
}

export function isValidArtistId(artistId: string): boolean {
  return isValidArtistSlug(artistId);
}

export function isValidEmail(email: string | undefined): email is string {
  const trimmed = email?.trim();
  return Boolean(trimmed && trimmed.includes('@'));
}

/** Normalize phone to E.164 for OneSignal.User.addSms (India default). */
export function normalizePhoneForOneSignal(phone: string | undefined): string | null {
  const trimmed = phone?.trim();
  if (!trimmed) return null;

  try {
    const parsed = parsePhoneNumber(trimmed, 'IN');
    if (parsed?.isValid()) return parsed.format('E.164');
  } catch {
    // fall through to digit-only fallback
  }

  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) {
    try {
      const parsed = parsePhoneNumber(`+91${digits}`);
      if (parsed?.isValid()) return parsed.format('E.164');
    } catch {
      return null;
    }
  }

  return null;
}

export interface ParsedSyncParams {
  artistId: string;
  email?: string;
  phone?: string;
  needsLogin: boolean;
  needsEmail: boolean;
  needsPhone: boolean;
}

export function parseSyncParams(params: SyncOneSignalUserParams): ParsedSyncParams | null {
  const artistId = params.artistId.trim();
  if (!isValidArtistId(artistId)) return null;

  const email = isValidEmail(params.email) ? params.email.trim() : undefined;
  const phone = normalizePhoneForOneSignal(params.phone) ?? undefined;

  const needsLogin = syncedExternalId !== artistId;
  const needsEmail = Boolean(email && email !== lastSyncedEmail);
  const needsPhone = Boolean(phone && phone !== lastSyncedPhone);

  if (!needsLogin && !needsEmail && !needsPhone) return null;

  return { artistId, email, phone, needsLogin, needsEmail, needsPhone };
}

export async function runSerializedSync(task: () => Promise<void>): Promise<void> {
  if (syncPromise) {
    await syncPromise;
  }

  syncPromise = task().finally(() => {
    syncPromise = null;
  });
  await syncPromise;
}
